/// <amd-module name="Build/Admin/Agent/Components/AgentCapabilitiesContainer" />
import * as React from "react";

import { caseInsensitiveContains } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";

import { Card, CustomCard, CardContent } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { CustomHeader, HeaderTitleArea, HeaderTitleRow, HeaderTitle, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";

import { TaskAgent, TaskAgentQueue, TaskAgentPool, TaskAgentPoolReference } from "DistributedTask/Client/Contracts/TaskAgent";

import { AgentActionCreator } from "Build/Admin/Agent/Actions/AgentActionCreator";
import { AddUserCapabilityPanel } from "Build/Admin/Agent/Components/AddUserCapabilityPanel";
import { CapabilitiesList, AgentCapability } from "Build/Admin/Agent/Components/CapabilitiesList";
import { StoresHub } from "Build/Admin/Agent/Stores/StoresHub";
import { ConfirmButtonType } from "Build/Admin/Common/Actions/ConfirmationDialogActions";
import { ConfirmationDialog } from "Build/Admin/Common/Components/ConfirmationDialog";
import { isHostedOrPoolProviderBacked } from "Build/Admin/Common/TaskAgentPoolUtils";

import * as Resources from "Build/Admin/Agent/Resources";

export interface IAgentCapabilitiesContainerProps {
    actionCreator: AgentActionCreator;
    storesHub: StoresHub;
}

export interface IAgentCapabilitiesContainerState {
    systemCapabilityFilter: Filter;
    selectedAgent: TaskAgent | undefined;
    selectedAgentQueue: TaskAgentQueue | undefined;
    selectedAgentPool: TaskAgentPool | undefined;
    userCapabilities: AgentCapability[];
    systemCapabilities: AgentCapability[];
    isPanelOpen: boolean;
}

const enum CapabilityType {
    System,
    User
}

export class AgentCapabilitiesContainer extends React.Component<IAgentCapabilitiesContainerProps, IAgentCapabilitiesContainerState> {
    constructor(props: IAgentCapabilitiesContainerProps, context: IAgentCapabilitiesContainerState) {
        super(props, context);

        const systemCapabilityFilter = new Filter();
        this.state = this._getStateFromStores(systemCapabilityFilter);
    }

    public render(): JSX.Element {
        const showSystemCard: boolean =
            (this.state.selectedAgentPool && !isHostedOrPoolProviderBacked(this.state.selectedAgentPool))
            || false;

        return (
            <div className="pipelines-agent-capabilities">
                <ConditionalChildren renderChildren={this.state.selectedAgent !== undefined}>
                    <div className="page-content page-content-top">
                        <Card
                            className="flex-grow"
                            titleProps={{ text: Resources.UserCapabilitiesCardTitle, size: TitleSize.Medium }}
                            headerCommandBarItems={this._getUserCapabilitiesCommandBarItems()}
                            contentProps={{ contentPadding: false }}>
                            {
                                this.state.userCapabilities.length > 0
                                ? <CapabilitiesList capabilities={this.state.userCapabilities} onCapabilityDeleted={this._confirmDeleteUserCapability} />
                                : this._renderZeroData()
                            }
                        </Card>
                    </div>
                </ConditionalChildren>
                <ConditionalChildren renderChildren={this.state.selectedAgent !== undefined && showSystemCard}>
                    <div className="page-content">
                        <CustomCard className="flex-grow bolt-card-with-header">
                            <CustomHeader className="bolt-card-header bolt-header-with-commandbar">
                                <HeaderTitleArea>
                                    <HeaderTitleRow>
                                        <HeaderTitle titleSize={TitleSize.Medium}>
                                            {Resources.SystemCapabilities}
                                        </HeaderTitle>
                                    </HeaderTitleRow>
                                </HeaderTitleArea>
                                <div className="bolt-header-commandbar flex-noshrink flex-row">
                                    <KeywordFilterBarItem className="capability-filter" filter={this.state.systemCapabilityFilter} placeholder={Resources.SearchByKeyword} filterItemKey="keyword" />
                                </div>
                            </CustomHeader>
                            <CardContent contentPadding={false}>
                                <CapabilitiesList capabilities={this.state.systemCapabilities} />
                            </CardContent>
                        </CustomCard>
                    </div>
                </ConditionalChildren>
                <AddUserCapabilityPanel isOpen={this.state.isPanelOpen} onUserCapabilityAdded={this._onUserCapabilityAdded} onDismiss={this._onPanelDismiss} />
                <ConfirmationDialog confirmationDialogStore={this.props.storesHub.confirmationDialogStore} />
            </div>
        );
    }

    public componentDidMount(): void {
        this.props.storesHub.selectionStore.addChangedListener(this._onStoreChanged);
        this.props.storesHub.addUserCapabilityPanelStore.addChangedListener(this._onStoreChanged);
        this.state.systemCapabilityFilter.subscribe(this._onStoreChanged, FILTER_CHANGE_EVENT);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.selectionStore.removeChangedListener(this._onStoreChanged);
        this.props.storesHub.addUserCapabilityPanelStore.removeChangedListener(this._onStoreChanged);
        this.state.systemCapabilityFilter.unsubscribe(this._onStoreChanged, FILTER_CHANGE_EVENT);
    }

    private _renderZeroData(): JSX.Element {
        return (
            <div className="pipelines-agent-capabilities-zero-data">
                <div className={"flex-column flex-center"}>
                    <div className="icon-wrapper">
                        <Icon className="zero-data-icon" iconName="List" />
                    </div>
                    <div className={"fontSizeL"}>{Resources.NoUserCapabilities}</div>
                    <Link className="fontSizeM" onClick={() => this.props.actionCreator.showAddUserCapabilityPanel()}>
                        {Resources.AddUserCapabilityLink}
                    </Link>
                </div>
            </div>
        );
    }

    private _getUserCapabilitiesCommandBarItems(): IHeaderCommandBarItem[] {
        return [
            {
                id: "addUserCapability",
                iconProps: {
                    iconName: "Add"
                },
                onActivate: () => {
                    this.props.actionCreator.showAddUserCapabilityPanel();
                }
            }
        ];
    }

    private _onPanelDismiss = (): void => {
        this.props.actionCreator.dismissAddUserCapabilityPanel();
    }

    private _onUserCapabilityAdded = (name: string, value: string): void => {
        const { selectedAgentQueue, selectedAgent, selectedAgentPool } = this.state;
        const pool: TaskAgentPoolReference | undefined = selectedAgentQueue ? selectedAgentQueue.pool : selectedAgentPool;
        if (selectedAgent && pool) {
            this.props.actionCreator.addUserCapability(pool, selectedAgent, name, value);
        }
    }

    private _confirmDeleteUserCapability = (capability: AgentCapability): void => {
        if (capability) {
            const content: JSX.Element = (
                <FormatComponent format={Resources.ConfirmDeleteUserCapabilityMessage} >
                    <span className="fontWeightSemiBold">{capability.name}</span>
                    <br />
                    <br />
                </FormatComponent>
            );

            this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
                capability.name,
                Resources.ConfirmDeleteUserCapabilityTitle,
                content,
                ConfirmButtonType.Delete,
                this._onUserCapabilityDeleted,
                this._dismissConfirmationDialog);
        }
    }

    private _onUserCapabilityDeleted = (capabilityName: string): void => {
        const { selectedAgentQueue, selectedAgent, selectedAgentPool } = this.state;
        const pool = selectedAgentQueue ? selectedAgentQueue.pool : selectedAgentPool
        if (selectedAgent && pool) {
            this.props.actionCreator.deleteUserCapability(pool, selectedAgent, capabilityName);
            this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
        }
    }

    private _dismissConfirmationDialog = (): void => {
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }

    private _onStoreChanged = (): void => {
        this.setState(this._getStateFromStores(this.state.systemCapabilityFilter));
    }

    private _getStateFromStores(systemCapabilityFilter: Filter): IAgentCapabilitiesContainerState {
        const { selectedAgent, selectedAgentQueue, selectedAgentPool } = this.props.storesHub.selectionStore.state;
        return {
            systemCapabilityFilter,
            selectedAgent,
            selectedAgentQueue,
            selectedAgentPool,
            userCapabilities: this._getAgentCapabilityItems(selectedAgent, CapabilityType.User),
            systemCapabilities: this._getAgentCapabilityItems(selectedAgent, CapabilityType.System, systemCapabilityFilter),
            isPanelOpen: this.props.storesHub.addUserCapabilityPanelStore.state.isOpen
        } as IAgentCapabilitiesContainerState;
    }

    private _getAgentCapabilityItems(agent: TaskAgent | undefined, capabilityType: CapabilityType, filter?: Filter | undefined): AgentCapability[] {
        const agentCapabilities: AgentCapability[] = [];

        if (agent) {
            let capabilities: { [key: string]: string } | undefined;

            if (capabilityType === CapabilityType.User) {
                capabilities = agent.userCapabilities;
            }
            else if (capabilityType === CapabilityType.System) {
                capabilities = agent.systemCapabilities;
            }

            if (capabilities) {
                const filterString: string | undefined = filter ? filter.getFilterItemValue("keyword") : undefined;

                for (const key of Object.keys(capabilities)) {
                    if (!filterString || caseInsensitiveContains(key, filterString) || caseInsensitiveContains(capabilities[key], filterString)) {
                        agentCapabilities.push({
                            name: key,
                            value: capabilities[key]
                        });
                    }
                }
            }
        }

        return agentCapabilities;
    }
}
